<template>
  <div
      v-if="maxTeamUsers"
      class="card-team-management"
      :class="{'not-editable': editSection !== ''}"
      style="position: relative"
  >
    <b-loading
        :active="editSection !== ''"
        animation="unset"
        :is-full-page="false"
    >
      <slot>{{''|upper}}</slot>
    </b-loading>

    <b-loading :is-full-page="false" :active="cardPreloaderIsActive" :can-cancel="false" />

    <div class="card-team-management-content">
      <div
          class="card-team-management-header"
      >
        <div :class="{ 'above-overlay': editUser || newUserFormIsActive }">
          <p style="color: #525F66">
            Here you can add or remove users who will have full or limited access to the account.
            Removed users will not have access to the account.
          </p>
        </div>
        <div class="personal-card-controls">
          <b-tooltip
              class="is-gray"
              type="is-info"
              :delay="200"
              position="is-top"
              :label="addButtonTooltip"
              animated
              multilined
          >
            <button
                class="button is-success add-user"
                :disabled="maxTeamUsers === null || teamUsers.length >= maxTeamUsers"
                @click="onAddUser"
            >
              ADD USER
            </button>
          </b-tooltip>
        </div>
      </div>

      <div class="card-team-management-users">
        <TeamManagementForm
            v-if="newUserFormIsActive"
            :resources="teamResources"
            :user="{}"
            :contractor-id="contractorId"
            @cancel-form="onFormCancel"
            @after-submit="afterFormSubmit"
        />

        <ul>
          <li
              v-for="user in teamUsers"
              :key="user.id"
          >
            <div class="list-user-content">
              <div class="list-user-info">
                <div class="list-user-name-and-access">
                  <div class="user-full-name">
                    <b>{{user.firstname}} {{user.lastname}}</b>
                  </div>

                    <div
                        class="user-access"
                        :class="{'user-full-access': user.access.type === 'Full'}"
                    >
                    <template v-if="user.contractorTeamData.isTeamUsersManagingAllowed">
                      (admin,
                    </template>
                    <template v-else>(</template>
                    <template v-if="user.access.type === 'Full'">full access)</template>
                    <template v-if="user.access.type === 'Limited'">limited access)</template>
                  </div>
                </div>

                <div class="user-email">
                  <i class="fa fa-envelope"></i>
                  <a :href="'mailto:' + user.email">{{user.email}}</a>
                </div>

                <div v-if="user.contractorTeamData.phone" class="user-phone">
                  <b-icon pack="fa" size="is-small" icon="phone" class="fa-rotate-90"></b-icon>
                  <a :href="`tel:${dialingPhoneNumber(user.contractorTeamData)}`">
                    {{ getPhoneFormatted(user.contractorTeamData.phone) }}
                  </a>
                </div>
              </div>

              <div
                  v-if="user.id !== currentUserId"
                  class="list-manage-box"
              >
                <button
                    class="list-manage-toggle-button"
                    :class="{'focus': manageUserId === user.id}"
                    @click="toggleActiveListManageBlockUserId(user.id)"
                >
                  <i class="fa fa-ellipsis-v"></i>
                </button>
                <div
                    class="list-manage"
                    :class="{'active': manageUserId === user.id}"
                >
                  <button class="list-manage-edit" @click="onEditUser(user)">
                    edit user
                  </button>
                  <button class="list-manage-delete" @click="() => onDeleteUser(user)">
                    delete user
                  </button>
                </div>
              </div>
            </div>

            <TeamManagementForm
                v-if="editUser && editUser.id === user.id"
                :resources="teamResources"
                :user="editUser"
                :contractor-id="contractorId"
                @cancel-form="onFormCancel"
                @after-submit="afterFormSubmit"
            />
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>

import { mapActions, mapMutations, mapState } from 'vuex';
import * as CONTRACTOR_ACTIONS from '@/store/modules/contractor/actions';
import * as MUTATION from '@/store/mutations';
import {
  teamMax,
  teamResources,
  teamUserDelete,
  teamUsers,
} from '@/api/contractor';
import TeamManagementForm from '@/components/Main/PersonalInfo/Card/TeamManagement/Form.vue';
import DeleteConfirmation from '@/components/Main/PersonalInfo/Card/TeamManagement/DeleteConfirmation.vue';
import Vue from 'vue';

const initialTitle = 'Team Management';
export default {
  name: 'CardTeamManagement',
  components: {
    TeamManagementForm,
  },
  props: {
    editSection: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: initialTitle,
    },
  },
  data() {
    return {
      id: null,
      cardFields: ['companyName', 'companyAddress', 'websiteUrl'],
      maxTeamUsers: null,
      teamUsers: [],
      teamResources: [],
      manageUserId: null,
      editUser: null,
      newUserFormIsActive: false,
      cardPreloaderIsActive: false,
      confirmModal: null,
      phone: '',
    };
  },
  computed: {
    ...mapState('contractor', { contractorId: (state) => state.id }),
    ...mapState('user', { currentUserId: (state) => state.id }),
    addButtonTooltip() {
      return this.maxTeamUsers === null || this.teamUsers.length >= this.maxTeamUsers
        ? 'You’ve reached the limit of additional users for your account.'
        : '';
    },
    getPhoneFormatted() {
      return (phone) => {
        if (phone) {
          return phone
            .replace(/^\+\d/, '')
            .replace(/^(\d{3})(\d{3})(\d+)$/, '$1-$2-$3');
        }
        return '';
      };
    },
    dialingPhoneNumber() {
      return (contractorTeamData) => {
        const [
          countryCode,
          phone,
        ] = this.getPhoneFromContractorTeamData(contractorTeamData);
        return countryCode + phone;
      };
    },
  },
  methods: {
    ...mapMutations({ preloader: MUTATION.SET_PRELOADER }),
    ...mapActions('contractor', { updateContractor: CONTRACTOR_ACTIONS.UPDATE_CONTRACTOR }),
    setSectionNotEditable() {
      this.cardFields.forEach((field) => {
        this[field] = this.$store.state.contractor[field];
      });
      this.editUser = null;
      this.newUserFormIsActive = false;
      this.$emit('edit-card', '');
    },
    updateIsActive() {
      this.$emit('update:is-active', !!this.maxTeamUsers);
    },
    updateTitle() {
      let newTitle = initialTitle;
      const maxUsers = this.maxTeamUsers === null ? 0 : this.maxTeamUsers;
      newTitle += ` (${this.teamUsers.length} of ${maxUsers} users)`;
      this.$emit('update:title', newTitle);
    },
    async loadData() {
      this.cardPreloaderIsActive = true;
      try {
        const maxPromise = teamMax(this.contractorId);
        const teamPromise = teamUsers(this.contractorId);
        const resourcesPromise = teamResources(this.contractorId);

        const promises = [maxPromise, teamPromise, resourcesPromise];
        const [maxResponse, teamResponse, resourcesResponse] = await Promise.all(promises);

        const { contractorMaxTeamUsers, tierMaxTeamUsers } = maxResponse.data.data;
        if (contractorMaxTeamUsers !== null) {
          this.maxTeamUsers = contractorMaxTeamUsers;
        } else {
          this.maxTeamUsers = tierMaxTeamUsers;
        }

        this.teamUsers = teamResponse.data.data;

        this.teamResources = resourcesResponse.data.data;

        this.updateTitle();
        this.updateIsActive();
      } catch (e) {
        this.$buefy.snackbar.open({
          message: 'Something wrong',
          actionText: ' ',
          position: 'is-top',
          indefinite: true,
          type: 'is-danger',
        });
      }
      this.cardPreloaderIsActive = false;
    },
    async onAddUser() {
      this.$emit('edit-card', this.title);
      await this.loadData();

      if (this.maxTeamUsers <= this.teamUsers.length) {
        this.$buefy.snackbar.open({
          message: 'You’ve reached the limit of additional users for your account',
          actionText: ' ',
          position: 'is-top',
          indefinite: true,
          type: 'is-danger',
        });
        this.$emit('edit-card', '');
      } else {
        this.newUserFormIsActive = true;
      }
    },
    onEditUser(user) {
      this.$emit('edit-card', this.title);
      this.editUser = user;
    },
    async onDeleteUser(user, confirm = false) {
      if (!confirm) {
        this.openConfirmModal(user);
        return;
      }

      this.preloader(true);
      try {
        await teamUserDelete(this.contractorId, user.id);
        this.$buefy.toast.open({ message: 'Update successful!', type: 'is-success', duration: 3000 });
      } catch (e) {
        this.$buefy.snackbar.open({
          message: 'Something wrong',
          actionText: ' ',
          position: 'is-top',
          indefinite: true,
          type: 'is-danger',
        });
      }

      this.confirmModal.$off('close', this.onConfirmClose);
      this.confirmModal.close();
      this.confirmModal = null;

      this.preloader(false);
      await this.loadData();
    },
    onFormCancel() {
      this.editUser = null;
      this.newUserFormIsActive = false;
      this.$emit('edit-card', '');
    },
    async afterFormSubmit() {
      this.editUser = null;
      this.newUserFormIsActive = false;
      this.$emit('edit-card', '');

      await this.loadData();
    },
    toggleActiveListManageBlockUserId(userId) {
      this.manageUserId = this.manageUserId === userId
        ? null
        : userId;
    },
    handleClick(event) {
      if (!event.target.className.includes('list-manage-toggle-button')) {
        this.manageUserId = null;
      }
    },
    openConfirmModal(user) {
      const Component = Vue.extend(DeleteConfirmation);
      this.confirmModal = this.$buefy.modal.open({
        parent: this,
        props: {
          user,
        },
        component: Component,
        hasModalCard: true,
        trapFocus: true,
        scroll: 'keep',
        ariaModal: true,
        customClass: 'team-management-delete-confirmation card-form-modal',
        canCancel: ['outside'],
        events: {
          confirm: () => {
            this.onDeleteUser(user, true);
          },
          cancel: () => {
            this.confirmModal.close();
          },
        },
      });
      this.confirmModal.$on('close', this.onConfirmClose);
    },
    onConfirmClose() {
      if (this.confirmModal) {
        this.confirmModal.$off('close', this.onConfirmClose);
        this.confirmModal = null;
      }
    },
    getPhoneFromContractorTeamData(contractorTeamData) {
      if (contractorTeamData) {
        let countryCode = '+1';
        let phone = '';
        if (contractorTeamData?.phone) {
          const code = contractorTeamData.phone.match(/^\+\d/);
          phone = contractorTeamData.phone;
          if (code) {
            countryCode = code.join('');
            phone = contractorTeamData.phone.replace(/^\+\d/, '');
          }
        }
        return [countryCode, phone];
      }
      return null;
    },
  },
  deactivated() {
    this.setSectionNotEditable();
  },
  async activated() {
    this.updateTitle();
    await this.loadData();
  },
  mounted() {
    document.addEventListener('click', this.handleClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClick);
  },
};
</script>

<style lang="stylus" scoped>
.card-team-management
  padding-top 15px

.card-team-management-header
  display flex
  justify-content space-between
  align-items center
  @media screen and (max-width 480px)
    flex-direction column
    align-items end
  button
    margin-left 15px
    @media screen and (max-width 480px)
      margin-left 0
      margin-top 15px
    &[disabled]
      background-color rgba(143,167,179,.3)
      opacity 1

.card-team-management-users
  margin-top 30px
  ul
    li
      padding 15px 0
      &:not(:last-child)
        border-bottom solid 1px rgba(128,128,128,0.349)
      .list-user-content
        display flex
        justify-content space-between
        align-items center
        .list-user-info
          width 95%
          .list-user-name-and-access
            display flex
          .user-full-name
            max-width calc(100% - 120px)
            white-space nowrap
            overflow hidden
            text-overflow ellipsis
            color #525F66
          .user-access
            margin-left 5px
            color #525F66
            &.user-full-access
              color #17b548
          .user-email
            display block
            margin-top 10px
            max-width calc(100% - 26px)
            white-space nowrap
            overflow hidden
            text-overflow ellipsis
            i
              color #8fa7b3
              margin-right 10px
          .user-phone
            span >>> i:before
              color #8fa7b3
            a
              display inline-block
              margin-left 10px;
        .list-manage-box
          display flex
          justify-content center
          align-items center
          position relative
          .list-manage-toggle-button
            background-color transparent
            color #68ba62
            border none
            font-size 22px
            cursor pointer
            &.focus
              color #B3DCB3
            &:hover
              color #17b548
              i
                pointer-events none
          .list-manage
            position absolute
            top 18px
            right -5px
            display none
            background-color white
            border 1px solid #9db2bc
            border-radius 5px
            width 150px
            padding 13px 5px 11px
            z-index 10
            &.active
              display block
            button
              background-color transparent
              border none
              text-transform uppercase
              text-align start
              cursor pointer
              font-size 16px
              &:not(:last-child)
                margin-bottom 8px
              &.list-manage-edit
                color #68BA62
              &.list-manage-delete
                color #DE4B4B
.personal-card-controls
  display flex
  justify-content flex-end

button.button
  width 135px
  height 48px
  border 1px solid #23D160
  font-size 19px
  text-transform uppercase
.button.is-success
  border none
.button.button-cancel
  color #23D160
.button.button-cancel:hover
  background #17b548
  color white
.loading-overlay >>> .loading-background
  background rgba(255,255,255,0.8)
.above-overlay
  z-index 10
</style>
